import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Switch as MaterialSwitch } from "@material-ui/core";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AccountingDayRoutingAction } from "actions/accountingDayRouting";

import { IntegrationType } from "constants/integrations";

import { Button, SecondaryButton } from "components/Form";
import { Row } from "components/Layout";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import SearchInput from "components/SearchInput";
import { Text } from "components/Text";

import {
  getAccountingDayRoutingById,
  getBusinesses,
  getCurrentSale,
  getDefaultBillingSettings,
  getIntegrationBusinessesByBusinessIdLookupByType,
  getIsFetchingAccountingDayRouting,
  getPayments,
  getSaleLots,
  selectBuyerInvoiceTotalCentsBySaleLotIdLookup,
  selectHasExportedInvoiceByBuyerIdLookup,
} from "selectors";

import toast from "lib/toast";

import { BuyerColumn } from "./BuyerColumn";
import {
  buildConfirmPayload,
  filterAndSortSuggestions,
  findSaleLotsForBuyer,
  getIndividualColumnReasons,
  resolveBuyerDestination,
  RoutingSide,
  summarizeBuyerSaleLots,
} from "./routing";
import {
  ColumnTitle,
  Columns,
  ColumnTitleCell,
  ColumnTitlesRow,
  MoveButton,
  MoveColumn,
  SearchWrapper,
  Toolbar,
} from "./styles";

interface AccountingDayRoutingModalProps {
  onClose: () => void;
}

export function AccountingDayRouting({
  onClose,
}: AccountingDayRoutingModalProps) {
  const dispatch = useDispatch();
  const currentSale = useSelector(getCurrentSale);
  const billingSettings = useSelector(getDefaultBillingSettings);
  const suggestionsById = useSelector(getAccountingDayRoutingById);
  const isFetching = useSelector(getIsFetchingAccountingDayRouting);
  const saleLotsById = useSelector(getSaleLots);
  const businessesById = useSelector(getBusinesses);
  const paymentsById = useSelector(getPayments);
  const invoiceTotalCentsBySaleLotId = useSelector(
    selectBuyerInvoiceTotalCentsBySaleLotIdLookup,
  );
  const hasExportedInvoiceByBuyerId = useSelector(
    selectHasExportedInvoiceByBuyerIdLookup,
  );
  const xeroIntegrationBusinessesByBuyerId = useSelector(
    getIntegrationBusinessesByBusinessIdLookupByType(IntegrationType.Xero),
  );

  const livestockSaleId = currentSale?.id;
  const cashBuyerBusinessId = billingSettings?.cashBuyerBusinessId || null;

  const [searchText, setSearchText] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [explicitDestinationByBuyerId, setExplicitDestinationByBuyerId] =
    useState<Record<string, RoutingSide>>({});
  const [isSelectedByBuyerId, setIsSelectedByBuyerId] = useState<
    Record<string, boolean>
  >({});

  const suggestions = useMemo(
    () => Object.values(suggestionsById || {}),
    [suggestionsById],
  );

  const saleLotsByBuyerId = useMemo(
    () =>
      suggestions.reduce((saleLotsById_, suggestion) => {
        saleLotsById_[suggestion.id] = findSaleLotsForBuyer(
          saleLotsById,
          livestockSaleId,
          suggestion.id,
          cashBuyerBusinessId,
        );
        return saleLotsById_;
      }, {}),
    [suggestions, saleLotsById, livestockSaleId, cashBuyerBusinessId],
  );

  const saleLotSummaryByBuyerId = useMemo(
    () =>
      suggestions.reduce((summaries, suggestion) => {
        summaries[suggestion.id] = summarizeBuyerSaleLots(
          saleLotsByBuyerId[suggestion.id] || [],
          suggestion.id,
          paymentsById,
          invoiceTotalCentsBySaleLotId,
          Boolean(hasExportedInvoiceByBuyerId[suggestion.id]),
        );
        return summaries;
      }, {}),
    [
      suggestions,
      saleLotsByBuyerId,
      paymentsById,
      invoiceTotalCentsBySaleLotId,
      hasExportedInvoiceByBuyerId,
    ],
  );

  const reasonsByBuyerId = useMemo(
    () =>
      suggestions.reduce((reasonsById, suggestion) => {
        const isExistingXeroContact = Boolean(
          xeroIntegrationBusinessesByBuyerId?.[suggestion.id]?.some(
            integrationBusiness => integrationBusiness.integrationId,
          ),
        );
        reasonsById[suggestion.id] = getIndividualColumnReasons(
          suggestion,
          isExistingXeroContact,
        );
        return reasonsById;
      }, {}),
    [suggestions, xeroIntegrationBusinessesByBuyerId],
  );

  const buyerNameById = useMemo(
    () =>
      suggestions.reduce((names, suggestion) => {
        names[suggestion.id] = businessesById?.[suggestion.id]?.name || "";
        return names;
      }, {}),
    [suggestions, businessesById],
  );

  const individualColumnRows = useMemo(
    () =>
      filterAndSortSuggestions(
        suggestions,
        RoutingSide.Individual,
        explicitDestinationByBuyerId,
        searchText,
        showFlaggedOnly,
        buyerNameById,
        saleLotSummaryByBuyerId,
      ),
    [
      suggestions,
      explicitDestinationByBuyerId,
      searchText,
      showFlaggedOnly,
      buyerNameById,
      saleLotSummaryByBuyerId,
    ],
  );

  const cashAccountColumnRows = useMemo(
    () =>
      filterAndSortSuggestions(
        suggestions,
        RoutingSide.Cash,
        explicitDestinationByBuyerId,
        searchText,
        showFlaggedOnly,
        buyerNameById,
        saleLotSummaryByBuyerId,
      ),
    [
      suggestions,
      explicitDestinationByBuyerId,
      searchText,
      showFlaggedOnly,
      buyerNameById,
      saleLotSummaryByBuyerId,
    ],
  );

  useEffect(() => {
    if (!livestockSaleId) return undefined;

    dispatch(AccountingDayRoutingAction.request({ livestockSaleId }));
    return () => {
      dispatch(AccountingDayRoutingAction.reset());
    };
  }, [dispatch, livestockSaleId]);

  const moveBuyersToSide = (buyerIds: string[], side: RoutingSide) => {
    if (buyerIds.length === 0) {
      return;
    }

    setExplicitDestinationByBuyerId(previousDestinations => {
      const nextDestinations = { ...previousDestinations };
      buyerIds.forEach(buyerId => {
        nextDestinations[buyerId] = side;
      });
      return nextDestinations;
    });

    setIsSelectedByBuyerId({});
  };

  const toggleBuyerSelected = (buyerId: string) => {
    setIsSelectedByBuyerId(previousSelection => {
      const nextSelection = { ...previousSelection };
      if (nextSelection[buyerId]) {
        delete nextSelection[buyerId];
      } else {
        nextSelection[buyerId] = true;
      }
      return nextSelection;
    });
  };

  const toggleSelectAllInRows = (rows: AccountingDayRoutingSuggestion[]) => {
    setIsSelectedByBuyerId(previousSelection => {
      const nextSelection = { ...previousSelection };
      const allRowsSelected =
        rows.length > 0 && rows.every(row => nextSelection[row.id]);

      rows.forEach(row => {
        if (allRowsSelected) {
          delete nextSelection[row.id];
        } else {
          nextSelection[row.id] = true;
        }
      });

      return nextSelection;
    });
  };

  const handleToggleShowFlaggedOnly = () =>
    setShowFlaggedOnly(previous => !previous);
  const handleMoveIndividualBuyerToCash = (buyerId: string) =>
    moveBuyersToSide([buyerId], RoutingSide.Cash);
  const handleMoveCashBuyerToIndividual = (buyerId: string) =>
    moveBuyersToSide([buyerId], RoutingSide.Individual);

  const selectedIndividualBuyerIds = individualColumnRows
    .filter(row => isSelectedByBuyerId[row.id])
    .map(row => row.id);
  const selectedCashAccountBuyerIds = cashAccountColumnRows
    .filter(row => isSelectedByBuyerId[row.id])
    .map(row => row.id);

  const handleMoveSelectedIndividualBuyersToCash = () =>
    moveBuyersToSide(selectedIndividualBuyerIds, RoutingSide.Cash);
  const handleMoveSelectedCashBuyersToIndividual = () =>
    moveBuyersToSide(selectedCashAccountBuyerIds, RoutingSide.Individual);

  const confirm = () => {
    if (!cashBuyerBusinessId) {
      toast.error("This deployment has no Cash Account business configured.");
      onClose();
      return;
    }

    const routingByBuyerId: Record<string, boolean> = {};
    suggestions.forEach(suggestion => {
      const destination = resolveBuyerDestination(
        suggestion,
        explicitDestinationByBuyerId,
      );
      routingByBuyerId[suggestion.id] = destination === RoutingSide.Cash;
    });

    const { buyerIdsToRouteToCash, buyerIdsToRouteToIndividual } =
      buildConfirmPayload(
        suggestions,
        routingByBuyerId,
        saleLotsById,
        livestockSaleId,
        cashBuyerBusinessId,
      );

    if (
      buyerIdsToRouteToCash.length > 0 ||
      buyerIdsToRouteToIndividual.length > 0
    ) {
      dispatch(
        AccountingDayRoutingAction.confirm({
          livestockSaleId,
          cashBuyerIds: buyerIdsToRouteToCash,
          individualBuyerIds: buyerIdsToRouteToIndividual,
        }),
      );
    } else {
      toast.info("No routing changes to apply.");
    }

    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose}>Assign Cash Sales</DialogTitle>
      <DialogContent dividers>
        {isFetching && "Loading suggested buyer routing..."}
        {!isFetching && !cashBuyerBusinessId && (
          <p>
            This deployment has no Cash Account business configured. Set one up
            before routing buyers here.
          </p>
        )}
        {!isFetching && cashBuyerBusinessId && suggestions.length === 0 && (
          <p>No buyers are ready for accounting-day review.</p>
        )}
        {!isFetching && cashBuyerBusinessId && suggestions.length > 0 && (
          <>
            <Toolbar>
              <SearchWrapper>
                <SearchInput value={searchText} handleChange={setSearchText} />
              </SearchWrapper>
            </Toolbar>
            <ColumnTitlesRow>
              <ColumnTitleCell>
                <ColumnTitle>Set as Individual Invoiced</ColumnTitle>
                <Row alignCenter gridGap={0}>
                  <Text color="gray44" fontSize="alpha">
                    Show only Suggested Cash Buyers
                  </Text>
                  <MaterialSwitch
                    data-tour="accounting-day-suggest-cash-buyer"
                    checked={showFlaggedOnly}
                    color="primary"
                    onChange={handleToggleShowFlaggedOnly}
                  />
                </Row>
              </ColumnTitleCell>
              <div />
              <ColumnTitleCell>
                <ColumnTitle>Set as Cash Buyer</ColumnTitle>
              </ColumnTitleCell>
            </ColumnTitlesRow>
            <Columns>
              <BuyerColumn
                side={RoutingSide.Individual}
                rows={individualColumnRows}
                isSelectedByBuyerId={isSelectedByBuyerId}
                onToggleSelected={toggleBuyerSelected}
                onSelectAll={toggleSelectAllInRows}
                onMoveRow={handleMoveIndividualBuyerToCash}
                saleLotSummaryByBuyerId={saleLotSummaryByBuyerId}
                reasonsByBuyerId={reasonsByBuyerId}
                emptyMessage="No buyers here."
                dataTour="accounting-day-select-all-individual"
              />
              <MoveColumn>
                <MoveButton
                  type="button"
                  title="Move to Cash Account"
                  data-tour="accounting-day-move-to-cash"
                  disabled={selectedIndividualBuyerIds.length === 0}
                  onClick={handleMoveSelectedIndividualBuyersToCash}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </MoveButton>
                <MoveButton
                  type="button"
                  title="Move to Individual Invoicing"
                  data-tour="accounting-day-move-to-individual"
                  disabled={selectedCashAccountBuyerIds.length === 0}
                  onClick={handleMoveSelectedCashBuyersToIndividual}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </MoveButton>
              </MoveColumn>
              <BuyerColumn
                side={RoutingSide.Cash}
                rows={cashAccountColumnRows}
                isSelectedByBuyerId={isSelectedByBuyerId}
                onToggleSelected={toggleBuyerSelected}
                onSelectAll={toggleSelectAllInRows}
                onMoveRow={handleMoveCashBuyerToIndividual}
                saleLotSummaryByBuyerId={saleLotSummaryByBuyerId}
                reasonsByBuyerId={reasonsByBuyerId}
                emptyMessage="Select buyers on the left and move them here."
                dataTour="accounting-day-select-all-cash"
              />
            </Columns>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton data-tour="close" type="button" onClick={onClose}>
          Close
        </SecondaryButton>
        <Button
          type="button"
          data-tour="accounting-day-confirm"
          onClick={confirm}
          disabled={
            isFetching || !cashBuyerBusinessId || suggestions.length === 0
          }
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
